(() => {
  const CAPSULE_BACKERS_BASE_URL = 'https://api.logistics.hungryminds.com/v1/shopify/backers/';

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }
    callback();
  };

  const toArray = (value) => Array.from(value || []);
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const getRoot = () => document.getElementById('root');
  const getBackersUrl = () => {
    const root = getRoot();
    return root ? (root.getAttribute('data-capsule-backers-url') || '').trim() : '';
  };

  const submitBackerForm = async (form, email) => {
    const backersUrl = getBackersUrl();
    if (!backersUrl) {
      return { success: false, message: 'Backers URL is not configured' };
    }

    const formData = new FormData(form);
    formData.set('email', email);

    try {
      const response = await fetch(CAPSULE_BACKERS_BASE_URL + backersUrl, {
        method: 'POST',
        body: formData,
      });
      const json = await response.json();
      if (json && json.success) {
        return { success: true };
      }
      return { success: false, message: 'Failed to send. Please try again.' };
    } catch (_error) {
      return { success: false, message: 'Failed to send. Please try again.' };
    }
  };

  const ensureNode = (parent, selector, tagName, className, text) => {
    if (!parent) return null;
    let node = parent.querySelector(selector);
    if (node) return node;

    node = document.createElement(tagName);
    node.className = className;
    if (typeof text === 'string') {
      node.textContent = text;
    }
    node.hidden = true;
    parent.appendChild(node);
    return node;
  };

  const initStickyHeader = () => {
    const header = document.querySelector('header.sticky.top-0.z-40.transition-all.duration-300.bg-background');
    if (!header) return;

    let rafId = 0;

    const applyState = () => {
      const isScrolled = window.scrollY > 8;
      header.classList.toggle('backdrop-blur-md', isScrolled);
      header.classList.toggle('shadow-sm', isScrolled);
    };

    const requestApply = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        applyState();
      });
    };

    applyState();
    window.addEventListener('scroll', requestApply, { passive: true });
    window.addEventListener('resize', requestApply);
  };

  const initSmoothScrollLinks = () => {
    const header = document.querySelector('header.sticky.top-0.z-40.transition-all.duration-300.bg-background');
    const prefersReducedMotion =
      'matchMedia' in window && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = prefersReducedMotion ? 'auto' : 'smooth';

    const resolveHashTarget = (hashValue) => {
      if (typeof hashValue !== 'string') return '';
      const cleaned = hashValue.trim();
      if (!cleaned) return '';
      if (cleaned === '#') return '#';
      if (cleaned.startsWith('#')) return cleaned;
      return '#' + cleaned;
    };

    const scrollToHash = (hashValue, pushToHistory) => {
      const normalizedHash = resolveHashTarget(hashValue);
      if (!normalizedHash) return false;

      if (normalizedHash === '#') {
        window.scrollTo({ top: 0, behavior: behavior });
        if (history.replaceState) {
          history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        return true;
      }

      const id = normalizedHash.slice(1);
      const target = document.getElementById(id);
      if (!target) return false;

      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const targetTop = target.getBoundingClientRect().top + window.scrollY - headerHeight - 8;
      window.scrollTo({ top: Math.max(0, targetTop), behavior: behavior });

      if (pushToHistory && history.pushState) {
        history.pushState(null, '', normalizedHash);
      }

      return true;
    };

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-scroll-to]');
      if (button) {
        const hash = button.getAttribute('data-scroll-to');
        if (hash && scrollToHash(hash, true)) {
          event.preventDefault();
        }
        return;
      }

      const link = event.target.closest('a[href]');
      if (!link) return;

      const href = (link.getAttribute('href') || '').trim();
      if (!href || href === 'javascript:void(0)') return;

      let url;
      try {
        url = new URL(href, window.location.href);
      } catch (_error) {
        return;
      }

      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
        return;
      }

      if (!url.hash && href !== '#') return;

      event.preventDefault();
      if (href === '#') {
        scrollToHash('#', false);
        return;
      }

      scrollToHash(url.hash, true);
    });
  };

  const initMobileMenu = () => {
    const toggle = document.querySelector('[data-capsule-mobile-menu-toggle]');
    const menu =
      document.querySelector('[data-capsule-mobile-menu]') ||
      document.querySelector('header .fixed.inset-0.z-30.bg-background.pt-16.px-6.lg\\:hidden');
    if (!toggle || !menu) return;

    const menuIcon = toggle.querySelector('[data-capsule-mobile-menu-icon]');
    const closeIcon = toggle.querySelector('[data-capsule-mobile-close-icon]');

    let opened = false;
    let previousBodyOverflow = '';

    const setMenuState = (nextState) => {
      if (opened === nextState) return;
      opened = nextState;
      toggle.setAttribute('aria-expanded', opened ? 'true' : 'false');
      menu.setAttribute('aria-hidden', opened ? 'false' : 'true');

      if (opened) {
        previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        menu.style.display = 'block';
        menu.style.pointerEvents = 'auto';
        if (menuIcon) menuIcon.style.display = 'none';
        if (closeIcon) closeIcon.style.display = 'block';
      } else {
        document.body.style.overflow = previousBodyOverflow;
        menu.style.display = 'none';
        menu.style.pointerEvents = 'none';
        if (menuIcon) menuIcon.style.display = 'block';
        if (closeIcon) closeIcon.style.display = 'none';
      }
    };

    toggle.setAttribute('aria-expanded', 'false');
    menu.setAttribute('aria-hidden', 'true');
    menu.style.display = 'none';
    menu.style.pointerEvents = 'none';
    if (menuIcon) menuIcon.style.display = 'block';
    if (closeIcon) closeIcon.style.display = 'none';

    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      setMenuState(!opened);
    });

    toArray(menu.querySelectorAll('a[href]')).forEach((link) => {
      link.addEventListener('click', () => {
        setMenuState(false);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && opened) {
        setMenuState(false);
      }
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth >= 1024 && opened) {
        setMenuState(false);
      }
    });
  };

  const initRevealAnimations = () => {
    const targets = toArray(document.querySelectorAll('[style*="opacity"]'))
      .filter((target) => !target.hasAttribute('data-no-reveal'))
      .filter((target) => {
        const inlineStyle = (target.getAttribute('style') || '').replace(/\s+/g, '').toLowerCase();
        return inlineStyle.includes('opacity:0');
      });
    if (!targets.length) return;

    const reveal = (element) => {
      element.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          element.style.opacity = '1';
          element.style.transform = 'none';
        });
      });
    };

    if (!('IntersectionObserver' in window)) {
      targets.forEach(reveal);
      return;
    }

    const isDesktopViewport = window.innerWidth >= 1024;
    const observerThreshold = isDesktopViewport ? 0.15 : 0.08;
    const observerRootMargin = isDesktopViewport ? '0px 0px -8% 0px' : '0px 0px -6% 0px';

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: observerThreshold,
        rootMargin: observerRootMargin,
      }
    );

    targets.forEach((target) => observer.observe(target));
  };

  const initFaq = () => {
    const faqSection = document.getElementById('faqs');
    if (!faqSection) return;

    const container = faqSection.querySelector('.max-w-2xl');
    if (!container) return;

    const answerMap = {
      'is this a limited collection?':
        "Yes. Each capsule is produced in limited quantities. Once a drop sells out, it won't be restocked in the same form.",
      'can i buy pieces separately?':
        "No. Each capsule is intentionally designed as a complete composition. Individual pieces aren't available.",
      'will it look exactly like the photos?':
        'Yes. We prioritize color-accurate reproduction and premium materials to ensure a true-to-photo experience.',
    };

    const items = toArray(container.children).filter((item) => item.querySelector('button'));
    if (!items.length) return;

    let openedIndex = -1;

    const getPanel = (item, answerText) => {
      let panel = item.querySelector('[data-capsule-faq-panel]');
      if (panel) return panel;

      panel = document.createElement('div');
      panel.setAttribute('data-capsule-faq-panel', '');
      panel.style.display = 'grid';
      panel.style.gridTemplateRows = '0fr';
      panel.style.opacity = '0';
      panel.style.transition = 'grid-template-rows 0.24s ease, opacity 0.24s ease';

      const inner = document.createElement('div');
      inner.style.overflow = 'hidden';

      const text = document.createElement('p');
      text.className = 'font-body text-sm text-muted-foreground leading-relaxed pb-5';
      text.textContent = answerText;

      inner.appendChild(text);
      panel.appendChild(inner);
      item.appendChild(panel);

      return panel;
    };

    const render = () => {
      items.forEach((item, index) => {
        const button = item.querySelector('button');
        const question = (button ? button.textContent : '').trim().toLowerCase();
        const answerText = answerMap[question] || '';
        const panel = answerText ? getPanel(item, answerText) : null;
        const icon = button ? button.querySelector('svg') : null;
        const isOpen = index === openedIndex;

        if (button) {
          button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }

        if (panel) {
          panel.style.gridTemplateRows = isOpen ? '1fr' : '0fr';
          panel.style.opacity = isOpen ? '1' : '0';
        }

        if (icon) {
          icon.classList.toggle('rotate-180', isOpen);
        }
      });
    };

    items.forEach((item, index) => {
      const button = item.querySelector('button');
      if (!button) return;

      button.addEventListener('click', (event) => {
        event.preventDefault();
        openedIndex = openedIndex === index ? -1 : index;
        render();
      });
    });

    render();
  };

  const initWaitlistSectionForm = () => {
    const section = document.getElementById('waitlist');
    const form = section ? section.querySelector('form') : null;
    if (!form) return;

    const input = form.querySelector('input[type="email"]');
    const submit = form.querySelector('button[type="submit"]');
    if (!input || !submit) return;

    const parent = form.parentNode;
    const error = ensureNode(
      parent,
      '[data-capsule-newsletter-error]',
      'p',
      'error font-body text-[12px] text-destructive mt-3',
      'Please enter a valid email'
    );
    if (error) {
      error.setAttribute('data-capsule-newsletter-error', '');
    }

    const success = ensureNode(
      parent,
      '[data-capsule-newsletter-success]',
      'p',
      'success font-body text-[12px] text-primary mt-3',
      "You're on the list ✓"
    );
    if (success) {
      success.setAttribute('data-capsule-newsletter-success', '');
    }

    const defaultText = submit.textContent;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const email = input.value.trim();
      if (!isValidEmail(email)) {
        if (error) {
          error.textContent = 'Please enter a valid email';
          error.hidden = false;
        }
        return;
      }

      if (error) {
        error.hidden = true;
      }
      if (success) {
        success.hidden = true;
      }

      submit.disabled = true;
      submit.classList.add('is-loading');
      submit.textContent = 'Sending...';

      const result = await submitBackerForm(form, email);
      if (result.success) {
        form.reset();
        form.style.display = 'none';
        if (success) {
          success.hidden = false;
        }
        window.dataLayer.push({
          event: 'form_submitted',
          event_data: {
            status: 'success',
          }
        });
      } else if (error) {
        error.textContent = result.message;
        error.hidden = false;
      }

      submit.disabled = false;
      submit.classList.remove('is-loading');
      submit.textContent = defaultText;
    });

    input.addEventListener('input', () => {
      if (error && !error.hidden) {
        error.textContent = 'Please enter a valid email';
        error.hidden = true;
      }
    });
  };

  const createWaitlistModal = () => {
    const layer =
      document.querySelector('[data-capsule-modal-layer]') ||
      document.querySelector('.fixed.inset-0.z-50.flex.items-center.justify-center');
    if (!layer) {
      return { open: () => {}, close: () => {} };
    }

    layer.setAttribute('data-capsule-modal-layer', '');
    layer.setAttribute('aria-hidden', 'true');

    const overlay =
      layer.querySelector('[data-capsule-modal-overlay]') || layer.querySelector(':scope > .absolute.inset-0');
    const dialog = layer.querySelector('[data-capsule-modal-panel]') || layer.querySelector(':scope > .relative.z-10');
    if (!dialog) {
      return { open: () => {}, close: () => {} };
    }

    let close = layer.querySelector('[data-capsule-modal-close]');
    if (!close) {
      close = document.createElement('button');
      close.type = 'button';
      close.setAttribute('data-capsule-modal-close', '');
      close.setAttribute('aria-label', 'Close');
      close.textContent = '×';
      close.className = 'absolute right-3 top-2 text-foreground/70 hover:text-foreground text-3xl leading-none';
      dialog.appendChild(close);
    }

    const form = dialog.querySelector('form');
    const input = form ? form.querySelector('input[type="email"]') : null;
    const submit = form ? form.querySelector('button[type="submit"]') : null;
    if (!form || !input || !submit) {
      return { open: () => {}, close: () => {} };
    }

    const prefersReducedMotion =
      'matchMedia' in window && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const transitionDurationMs = prefersReducedMotion ? 0 : 280;
    const transitionValue = prefersReducedMotion
      ? 'none'
      : 'opacity 0.28s cubic-bezier(0.22, 1, 0.36, 1), transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)';

    layer.style.opacity = '0';
    layer.style.pointerEvents = 'none';
    layer.style.transition = prefersReducedMotion
      ? 'none'
      : 'opacity 0.28s cubic-bezier(0.22, 1, 0.36, 1)';
    if (overlay) {
      overlay.style.opacity = '0';
      overlay.style.transition = prefersReducedMotion
        ? 'none'
        : 'opacity 0.28s cubic-bezier(0.22, 1, 0.36, 1)';
    }
    dialog.style.opacity = '0';
    dialog.style.transform = 'translateY(14px) scale(0.985)';
    dialog.style.transition = transitionValue;

    let opened = false;
    let closeTimeout = 0;
    let previousBodyOverflow = '';
    const defaultSubmitText = submit.textContent;

    const error = ensureNode(
      dialog,
      '[data-capsule-modal-error]',
      'p',
      'error font-body text-[12px] text-destructive mt-3',
      'Please enter a valid email'
    );
    if (error) {
      error.setAttribute('data-capsule-modal-error', '');
    }

    const success = ensureNode(
      dialog,
      '[data-capsule-modal-success]',
      'p',
      'success font-body text-[12px] text-primary mt-3',
      "You're on the list ✓"
    );
    if (success) {
      success.setAttribute('data-capsule-modal-success', '');
    }

    const reset = () => {
      form.reset();
      form.hidden = false;
      if (error) {
        error.hidden = true;
        error.textContent = 'Please enter a valid email';
      }
      if (success) {
        success.hidden = true;
      }
      submit.disabled = false;
      submit.classList.remove('is-loading');
      submit.textContent = defaultSubmitText;
    };

    const open = () => {
      if (opened) return;
      opened = true;
      reset();
      layer.setAttribute('aria-hidden', 'false');
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      if (closeTimeout) {
        window.clearTimeout(closeTimeout);
        closeTimeout = 0;
      }

      requestAnimationFrame(() => {
        if (!opened) return;
        layer.style.opacity = '1';
        layer.style.pointerEvents = 'auto';
        if (overlay) {
          overlay.style.opacity = '1';
        }
        dialog.style.opacity = '1';
        dialog.style.transform = 'translateY(0) scale(1)';
      });

      window.setTimeout(() => {
        input.focus();
      }, 40);
    };

    const closeModal = () => {
      if (!opened) return;
      opened = false;
      layer.setAttribute('aria-hidden', 'true');
      layer.style.opacity = '0';
      layer.style.pointerEvents = 'none';
      if (overlay) {
        overlay.style.opacity = '0';
      }
      dialog.style.opacity = '0';
      dialog.style.transform = 'translateY(14px) scale(0.985)';

      if (closeTimeout) {
        window.clearTimeout(closeTimeout);
      }

      if (!transitionDurationMs) {
        document.body.style.overflow = previousBodyOverflow;
        return;
      }

      closeTimeout = window.setTimeout(() => {
        closeTimeout = 0;
        if (!opened) {
          document.body.style.overflow = previousBodyOverflow;
        }
      }, transitionDurationMs);
    };

    close.addEventListener('click', closeModal);
    layer.addEventListener('click', (event) => {
      if (event.target === layer || event.target === overlay) {
        closeModal();
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const email = input.value.trim();
      if (!isValidEmail(email)) {
        if (error) {
          error.textContent = 'Please enter a valid email';
          error.hidden = false;
        }
        return;
      }

      if (error) {
        error.hidden = true;
      }
      if (success) {
        success.hidden = true;
      }

      submit.disabled = true;
      submit.classList.add('is-loading');
      submit.textContent = 'Sending...';

      const result = await submitBackerForm(form, email);
      if (result.success) {
        form.reset();
        form.hidden = true;
        if (success) {
          success.hidden = false;
        }

        window.dataLayer.push({
          event: 'form_submitted',
          event_data: {
            status: 'success',
          }
        });
      } else if (error) {
        error.textContent = result.message;
        error.hidden = false;
      }

      submit.disabled = false;
      submit.classList.remove('is-loading');
      submit.textContent = defaultSubmitText;
    });

    input.addEventListener('input', () => {
      if (error && !error.hidden) {
        error.textContent = 'Please enter a valid email';
        error.hidden = true;
      }
    });

    return { open: open, close: closeModal };
  };

  const initTriggers = (modalApi) => {
    if (!modalApi) return;

    const buttons = toArray(document.querySelectorAll('button'));
    const triggers = buttons.filter((button) => {
      if (!button) return false;

      const typeAttr = (button.getAttribute('type') || '').trim().toLowerCase();
      const isSubmitInForm = Boolean(button.closest('form')) && (typeAttr === '' || typeAttr === 'submit');
      if (isSubmitInForm) return false;

      if (button.hasAttribute('data-track')) {
        const track = button.getAttribute('data-track');
        if (track === 'add_to_cart' || track === 'open_mini_cart') return true;
      }

      const text = (button.textContent || '').trim().toLowerCase();
      return text === 'join waitlist' || text === 'join waitlist for circle dance';
    });

    triggers.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        modalApi.open();
      });
    });
  };

  const initCapsulePage = () => {
    const root = getRoot();
    if (!root || root.dataset.capsuleReady === 'true') {
      return;
    }
    root.dataset.capsuleReady = 'true';

    initStickyHeader();
    initMobileMenu();
    initSmoothScrollLinks();
    initRevealAnimations();
    initFaq();
    initWaitlistSectionForm();

    const modalApi = createWaitlistModal();
    initTriggers(modalApi);
  };

  onReady(initCapsulePage);
})();
